import { ReviewStatus } from '@skop-app/lib-types'

import type { OptionRow } from '@/services/property/property.type'
import {
  OPTION_ARCHIVED_STATUS,
  OPTION_RETURNED_STATUS,
} from '@/services/property/option.status'
import type { Translator } from '@/types'

import type { OptionGridContext } from './optionGrid.type'
import {
  resolveOptionListAccess,
  resolveOptionPermissions,
} from './option.rules'

export type OptionModerationReasonCode =
  | 'missing-update-right'
  | 'missing-delete-right'
  | 'owner-unresolved'
  | 'limited-row-scope'
  | 'not-list-owner'
  | 'status-not-eligible'
  | 'replacement-required'
  | 'replacement-single-only'
  | 'usage-unresolved'
  | 'no-replacement-candidate'

type OptionActionAvailability = {
  enabled: boolean
  reasonCode?: OptionModerationReasonCode
}

export type OptionModerationAvailability = {
  canOpenComments: boolean
  accept: OptionActionAvailability
  refuseValidation: OptionActionAvailability
  resubmit: OptionActionAvailability
  archive: OptionActionAvailability
  delete: OptionActionAvailability & {
    mode: 'disabled' | 'direct' | 'replacement'
  }
}

export const getOptionModerationReasonLabel = (
  t: Translator,
  reasonCode?: OptionModerationReasonCode,
) => {
  if (!reasonCode) return undefined

  switch (reasonCode) {
    case 'missing-update-right':
      return t('Property.option-moderation-reason-missing-update-right')
    case 'missing-delete-right':
      return t('Property.option-moderation-reason-missing-delete-right')
    case 'owner-unresolved':
      return t('Property.option-moderation-reason-owner-unresolved')
    case 'limited-row-scope':
      return t('Property.option-moderation-reason-limited-row-scope')
    case 'not-list-owner':
      return t('Property.option-moderation-reason-not-list-owner')
    case 'status-not-eligible':
      return t('Property.option-moderation-reason-status-not-eligible')
    case 'replacement-required':
      return t('Property.option-moderation-reason-replacement-required')
    case 'replacement-single-only':
      return t('Property.option-moderation-reason-replacement-single-only')
    case 'usage-unresolved':
      return t('Property.option-moderation-reason-usage-unresolved')
    case 'no-replacement-candidate':
      return t('Property.option-moderation-reason-no-replacement-candidate')
    default:
      return undefined
  }
}

export type OptionDeletePlan = {
  directDeletableRows: OptionRow[]
  replacementRequiredRows: OptionRow[]
  blockedRows: Array<{
    row: OptionRow
    reasonCode: OptionModerationReasonCode
  }>
  unresolvedRows: OptionRow[]
}

const isPersistedOption = (row: OptionRow | undefined) =>
  Boolean(row?.id && !row.id.startsWith('new-'))

const getModerationAccessReason = (
  context: Pick<
    OptionGridContext,
    'authorization' | 'isListOwner' | 'isListOwnerResolved'
  >,
): OptionModerationReasonCode | null => {
  const optionsAuth = context.authorization?.optionsAuth
  const access = resolveOptionListAccess({
    canUpdate: Boolean(optionsAuth?.canUpdate),
    isListOwner: context.isListOwner,
    isListOwnerResolved: context.isListOwnerResolved,
  })

  if (access.canManageList) return null

  switch (access.restrictionReason) {
    case 'missing-update-right':
    case 'owner-unresolved':
    case 'limited-row-scope':
    case 'not-list-owner':
      return access.restrictionReason
    default:
      return 'not-list-owner'
  }
}

const getDeleteAccessReason = ({
  context,
  row,
}: {
  context: OptionGridContext
  row: OptionRow
}): OptionModerationReasonCode | null => {
  const permissions = resolveOptionPermissions({
    context,
    data: row,
  })
  if (permissions.canDeleteExisting) {
    return null
  }

  return getModerationAccessReason(context) ?? 'missing-delete-right'
}

export const resolveOptionDeletePlan = ({
  context,
  rows,
}: {
  context: OptionGridContext
  rows: OptionRow[]
}): OptionDeletePlan => {
  const directDeletableRows: OptionRow[] = []
  const replacementRequiredRows: OptionRow[] = []
  const blockedRows: Array<{
    row: OptionRow
    reasonCode: OptionModerationReasonCode
  }> = []
  const unresolvedRows: OptionRow[] = []

  rows.forEach((row) => {
    if (!isPersistedOption(row)) {
      blockedRows.push({
        row,
        reasonCode: 'status-not-eligible',
      })
      return
    }

    if (row.status === ReviewStatus.REJECTED || row.status === 'DELETED') {
      blockedRows.push({
        row,
        reasonCode: 'status-not-eligible',
      })
      return
    }

    const deleteReason = getDeleteAccessReason({ context, row })
    if (deleteReason) {
      blockedRows.push({
        row,
        reasonCode: deleteReason,
      })
      return
    }

    if (context.isReferenceUsageResolved === false) {
      unresolvedRows.push(row)
      return
    }

    if ((row.referencesUsageCount ?? 0) > 0) {
      replacementRequiredRows.push(row)
      return
    }

    directDeletableRows.push(row)
  })

  return {
    directDeletableRows,
    replacementRequiredRows,
    blockedRows,
    unresolvedRows,
  }
}

export const resolveOptionModerationAvailability = ({
  context,
  row,
  selectionSize = 1,
  hasReplacementCandidate = true,
}: {
  context: OptionGridContext
  row?: OptionRow
  selectionSize?: number
  hasReplacementCandidate?: boolean
}): OptionModerationAvailability => {
  const moderationReason = getModerationAccessReason(context)
  const persisted = isPersistedOption(row)
  const status = row?.status
  const canOpenComments = persisted

  const acceptDisabledReason = !persisted
    ? 'status-not-eligible'
    : moderationReason ||
      (status === ReviewStatus.ACCEPTED ||
      status === OPTION_ARCHIVED_STATUS ||
      status === ReviewStatus.REJECTED ||
      status === 'DELETED'
        ? 'status-not-eligible'
        : undefined)

  const refuseDisabledReason = !persisted
    ? 'status-not-eligible'
    : moderationReason ||
      (status === OPTION_ARCHIVED_STATUS ||
      status === ReviewStatus.REJECTED ||
      status === OPTION_RETURNED_STATUS ||
      status === 'DELETED'
        ? 'status-not-eligible'
        : undefined)

  const resubmitPermissions = row
    ? resolveOptionPermissions({
        context,
        data: row,
      })
    : null
  const resubmitDisabledReason = !persisted
    ? 'status-not-eligible'
    : !resubmitPermissions?.canEditExisting
      ? (moderationReason ?? 'status-not-eligible')
      : status !== OPTION_RETURNED_STATUS
        ? 'status-not-eligible'
        : undefined

  const archivePlan = row
    ? resolveOptionDeletePlan({
        context,
        rows: [row],
      })
    : {
        directDeletableRows: [],
        replacementRequiredRows: [],
        blockedRows: [],
        unresolvedRows: [],
      }

  const archiveMode = (() => {
    if (!persisted) return 'disabled' as const
    if (archivePlan.directDeletableRows.length > 0) return 'direct' as const
    if (archivePlan.replacementRequiredRows.length > 0) {
      return 'replacement' as const
    }
    return 'disabled' as const
  })()

  const archiveReasonCode = (() => {
    if (!persisted) return 'status-not-eligible' as const
    if (archivePlan.blockedRows.length > 0) {
      return archivePlan.blockedRows[0]?.reasonCode
    }
    if (archivePlan.unresolvedRows.length > 0) {
      return context.loadReferenceUsageCounts ? undefined : 'usage-unresolved'
    }
    if (archivePlan.replacementRequiredRows.length > 0) {
      if (selectionSize > 1) return 'replacement-single-only'
      if (!hasReplacementCandidate) return 'no-replacement-candidate'
      return undefined
    }
    return archivePlan.directDeletableRows.length > 0
      ? undefined
      : ('status-not-eligible' as const)
  })()

  return {
    canOpenComments,
    accept: {
      enabled: !acceptDisabledReason,
      reasonCode: acceptDisabledReason,
    },
    refuseValidation: {
      enabled: !refuseDisabledReason,
      reasonCode: refuseDisabledReason,
    },
    resubmit: {
      enabled: !resubmitDisabledReason,
      reasonCode: resubmitDisabledReason,
    },
    archive: {
      enabled: Boolean(
        row &&
        persisted &&
        !moderationReason &&
        resolveOptionPermissions({ context, data: row }).canDeleteExisting &&
        !['ARCHIVED', 'DELETED', 'REJECTED'].includes(status ?? ''),
      ),
    },
    delete: {
      enabled: !archiveReasonCode,
      reasonCode: archiveReasonCode,
      mode: archiveReasonCode ? 'disabled' : archiveMode,
    },
  }
}
